import React from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

import { INTERVAL_TYPE_VALUES, CHART_TYPES } from '@mpm-druid/constants';

export const getDateRangeMessage = (tooltipData, tooltipVisible, from, to) => {
  try {
    if (!tooltipVisible) {
      return `${moment
        .utc(from)
        .local()
        .format('MMM DD, YYYY')} to ${moment
        .utc(to)
        .local()
        .format('MMM DD, YYYY')}`;
    }
    if (tooltipVisible)
      return moment
        .utc(tooltipData.x, 'YYYY-MM-DD')
        .local()
        .format('MMMM DD, YYYY');
  } catch (_) {
    return 'invalid date';
  }

  return 'invalid date';
};

export const makeLineChartNotifShape = (notification, lineColor) => {
  const point = moment(notification?.evaluationTimeRangeFrom).toISOString(true);
  if (isNil(point)) return null;
  return {
    type: 'line',
    xref: 'x',
    yref: 'paper',
    x0: point,
    x1: point,
    y0: 0.05,
    y1: 0.95,
    line: {
      color: lineColor || '#f14668',
      dash: '4px,4px',
      width: 1.0
    }
  };
};

export const makeLineChartTooltipShape = tooltipData => {
  const pointX = tooltipData.x;
  return {
    type: 'line',
    xref: 'x',
    yref: 'paper',
    x0: pointX,
    x1: pointX,
    y0: 0,
    y1: 0.95,
    line: {
      color: '#b3b9c8',
      dash: 'solid',
      width: 1.0
    }
  };
};

export const makeTriangleTooltipShape = tooltipData => {
  const { data, x } = tooltipData;
  // Another possible solution. But the second one looks more correct logically
  // const point = (data.pointIndex + 0.5) / data.data.x.length;
  const pointDate = data.xaxis.d2c(x);
  const tmin = data.xaxis._rl[0];
  const tmax = data.xaxis._rl[1];
  const point = (pointDate - tmin) / (tmax - tmin);
  return {
    type: 'path',
    xref: 'paper',
    yref: 'paper',
    path: `M ${point}, 0 L ${point + 0.0015}, -0.02 L ${
      point - 0.0015
    }, -0.02 Z`,
    fillcolor: '#8c95a8',
    line: {
      color: '#8c95a8'
    }
  };
};

const getISODateFromHours = hoursAgoFromToday => {
  return new Date(
    new Date().getTime() - hoursAgoFromToday * 60 * 60 * 1000
  ).toISOString();
};

export const makeLineChartShape = (
  enabledChartData,
  evaluationWindowHours,
  delayHours
) => {
  if (!enabledChartData) {
    return [];
  }
  const shapes = [];

  const rightRectPoint = getISODateFromHours(delayHours);
  const leftRectPoint = getISODateFromHours(evaluationWindowHours + delayHours);

  if (leftRectPoint < 1) return shapes;

  const alertRectangle = {
    type: 'rect',
    xref: 'x',
    yref: 'paper',
    x0: rightRectPoint,
    y0: 0,
    x1: leftRectPoint,
    y1: 1,
    fillcolor: '#A020F0',
    opacity: 0.2,
    line: {
      width: 0
    }
  };
  shapes.push(alertRectangle);

  return shapes;
};

export const addHoverBarsOpacity = (data, title, index) => {
  let hoverData = cloneDeep(data);
  const countBars = hoverData[0].x.length;
  const opacityUnactiveBars = Array(countBars).fill(0.4);
  const opacityActiveBars = [...opacityUnactiveBars];
  opacityActiveBars[index] = 1;
  hoverData = hoverData.map(element => {
    if (element.name === title && 'name' in element) {
      element.marker.opacity = opacityActiveBars;
    } else if ('name' in element && element.name !== title) {
      element.marker.opacity = opacityUnactiveBars;
    }
    return element;
  });
  return hoverData;
};

export const addHoverLinesOpacity = (data, title, id) => {
  let hoverData = cloneDeep(data);
  if (data?.length === 1) return hoverData;
  hoverData = hoverData.map(slice => {
    const { color } = slice?.line || {};
    if (!color) return slice;
    if (id && slice.query !== id && slice.name !== id) {
      slice.line.color = `${color}40`;
    }
    return slice;
  });
  return hoverData;
};

// Function to get the end of the day or current moment if it's in the future
function getEndOfDayOrCurrent(xValueString, formatString) {
  const momentDate = moment(xValueString, formatString);
  const currentMoment = moment();
  const endDayOfDate = momentDate.endOf('day');

  if (endDayOfDate.isAfter(currentMoment)) {
    return currentMoment.format('Do MMM, H:mm');
  } else {
    return endDayOfDate.format('Do MMM, H:mm');
  }
}

export const getTooltipHeaderTitle = ({
  headerTitle,
  xValue,
  xValuesList,
  intervalType,
  chartType,
  pointIdx
}) => {
  if (headerTitle) return headerTitle;
  if (
    intervalType === INTERVAL_TYPE_VALUES.DAILY &&
    (chartType === CHART_TYPES.LINE ||
      chartType === CHART_TYPES.LINE_WITH_PERCENTILE ||
      chartType === CHART_TYPES.STACKED ||
      chartType === CHART_TYPES.LINE_CATEGORICAL_BAR_DATA ||
      chartType === CHART_TYPES.STACKED_TO_LINES)
  ) {
    try {
      if (pointIdx === 0) {
        return `${moment(xValue, 'YYYY-MM-DD ha').format(
          'Do MMM, H:mm'
        )} to ${moment(xValuesList[pointIdx + 1]).format('Do MMM, H:mm')}`;
      }
      if (pointIdx === xValuesList?.length - 1) {
        return `${moment(xValue, 'YYYY-MM-DD ha').format(
          'Do MMM, H:mm'
        )} to ${getEndOfDayOrCurrent(xValue, 'YYYY-MM-DD ha')}`;
      }
      const range = `${moment(xValue, 'YYYY-MM-DD ha').format(
        'Do MMM, H:mm'
      )} to ${moment(xValue).add(1, 'days').format('Do MMM, H:mm')}`;
      return range;
    } catch (e) {
      return xValue;
    }
  }
  return intervalType === INTERVAL_TYPE_VALUES.HOURLY
    ? moment(xValue, 'YYYY-MM-DD HH:mm').format('Do MMM, ha')
    : moment(xValue, 'YYYY-MM-DD HH:mm').format('Do MMM');
};

const getDataValue = (data, propertyString) => {
  let path = propertyString.slice(
    propertyString.indexOf('{') + 1,
    propertyString.lastIndexOf('}')
  );
  if (path.split('.').length > 2) {
    path = path.slice(0, path.lastIndexOf('.'));
  }
  const searchValue = get(data, path);
  if (Number.isNaN(parseFloat(searchValue))) {
    return searchValue;
  }
  return parseFloat(searchValue).toFixed(4);
};

const generateTextData = rows => {
  return rows.map(row => {
    const isBold = row.startsWith('<b>');
    const text = row.replace(/<b>/g, '');
    const rowValue = isBold ? <b>{text}</b> : row;

    return (
      <span className="tooltip-data-value" key={row}>
        {rowValue}
      </span>
    );
  });
};

export const parseTooltipTemplate = (hovertemplate, text, y, data) => {
  const textData = { ...text };
  delete textData.date;

  if (isEmpty(textData) || !hovertemplate) {
    return <span className="tooltip-data-value">{y}</span>;
  }

  const rows = hovertemplate.split('<br>');
  rows.shift();

  const parsedRows = rows.map(row => {
    const [first, ...rest] = row.split(':');
    const splittedRow = [
      `${first.trim()}: `,
      getDataValue(data, rest.join(''))
    ];
    return splittedRow.join('');
  });

  return generateTextData(parsedRows);
};

export const getDelayValue = (newValue, prevValue, isDaily) => {
  if (newValue === '' || /^\d+$/.test(newValue)) {
    const maxLimit = isDaily ? 30 : 720;
    const numericValue = parseInt(newValue, 10);
    if (newValue === '' || numericValue <= maxLimit) {
      return newValue;
    }
    return maxLimit.toString();
  }
  return prevValue;
};

export const calculateBarsGap = data => {
  if (!data || data.length === 0) return 0.875;
  let maxDataLength = 0;

  for (let i = 0; i < data.length; i++) {
    const currentLength = data[i].x?.length;
    if (currentLength > maxDataLength) {
      maxDataLength = currentLength;
    }
  }

  if (maxDataLength >= 10 && maxDataLength <= 120) {
    const val = 0.7 - 0.1 * Math.round(maxDataLength / 30);
    return val >= 0.1 ? val : 0.1;
  }
  if (maxDataLength > 120 && maxDataLength < 130) {
    return 0.355;
  }
  if (maxDataLength >= 130 && maxDataLength <= 144) {
    return 0.45;
  }
  if (maxDataLength > 144) {
    return 0.5;
  }

  return 0.875;
};
